if (!customElements.get('generation-scroll')) {
  class GenerationScroll extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.generation-scroll__stage');
      this.viewport = this.querySelector('[data-generation-viewport]');
      this.track = this.querySelector('.generation-scroll__track');
      this.items = Array.from(this.querySelectorAll('[data-generation-item]'));
      this.previousButton = this.querySelector('[data-generation-previous]');
      this.nextButton = this.querySelector('[data-generation-next]');

      if (!this.stage || !this.viewport || !this.track || !this.items.length) return;

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.tabletQuery = window.matchMedia('(min-width: 768px)');
      this.desktopQuery = window.matchMedia('(min-width: 1280px)');
      this.activeIndex = -1;
      this.forceVisible = null;
      this.layoutMode = null;
      this.libraryAttempts = 0;

      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
      this.handleViewportScroll = this.handleViewportScroll.bind(this);
      this.handleViewportKeydown = this.handleViewportKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);

      const listenerOptions = { signal: this.abortController.signal };
      this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange, listenerOptions);
      window.addEventListener('load', this.handleWindowLoad, {
        once: true,
        signal: this.abortController.signal,
      });
      window.addEventListener('resize', this.handleResize, {
        passive: true,
        signal: this.abortController.signal,
      });
      this.viewport.addEventListener('scroll', this.handleViewportScroll, {
        passive: true,
        signal: this.abortController.signal,
      });
      this.viewport.addEventListener('keydown', this.handleViewportKeydown, listenerOptions);
      this.previousButton?.addEventListener('click', this.handlePrevious, listenerOptions);
      this.nextButton?.addEventListener('click', this.handleNext, listenerOptions);

      this.scheduleBuild();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.destroyAnimation();
      window.clearTimeout(this.libraryTimer);
      cancelAnimationFrame(this.libraryFrame);
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.refreshFrame);
    }

    handleWindowLoad() {
      if (!this.timeline && this.getLayoutMode() === 'desktop') this.scheduleBuild();
    }

    handleReducedMotionChange() {
      this.scheduleBuild();
    }

    handleResize() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        const nextLayoutMode = this.getLayoutMode();

        if (nextLayoutMode !== this.layoutMode) {
          this.scheduleBuild();
          return;
        }

        this.timeline?.scrollTrigger?.refresh();
      });
    }

    scheduleBuild() {
      window.clearTimeout(this.libraryTimer);
      cancelAnimationFrame(this.libraryFrame);
      this.libraryAttempts = 0;
      this.libraryFrame = requestAnimationFrame(() => this.buildForCurrentMode());
    }

    buildForCurrentMode() {
      if (!this.isConnected) return;

      if (this.reducedMotionQuery.matches || this.items.length < 2) {
        this.enableFallback();
        return;
      }

      if (this.getLayoutMode() !== 'desktop') {
        this.enableTouchCarousel();
        return;
      }

      this.waitForAnimationLibraries();
    }

    waitForAnimationLibraries() {
      if (!this.isConnected || this.getLayoutMode() !== 'desktop') return;

      if (window.gsap && window.ScrollTrigger) {
        this.buildAnimation();
        return;
      }

      if (this.libraryAttempts === 0) this.enableFallback();
      this.libraryAttempts += 1;

      // Consent tools and deferred theme bundles can expose GSAP after window.load.
      // Keep the readable native carousel active while checking at low frequency.
      if (this.libraryAttempts < 120) {
        this.libraryTimer = window.setTimeout(() => this.waitForAnimationLibraries(), 500);
      }
    }

    buildAnimation() {
      this.destroyAnimation();
      this.classList.add('is-enhanced');
      this.classList.remove('is-fallback', 'is-touch-carousel');
      this.layoutMode = 'desktop';
      this.activeIndex = -1;
      this.forceVisible = null;
      this.viewport.scrollLeft = 0;
      this.track.style.removeProperty('transform');

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);

      this.animationContext = gsap.context(() => {
        // The handoff gives each generation a short, deliberate scroll chapter.
        // Keep that cadence independent from media widths so the transition remains stable.
        const getSequenceDistance = () => Math.max(
          this.stage.clientHeight,
          this.stage.clientHeight * Math.max(1, this.items.length - 1)
        );
        const getHeaderOffset = () => Number.parseFloat(
          getComputedStyle(this).getPropertyValue('--generation-scroll-header-offset')
        ) || 0;

        this.timeline = gsap.to({}, {
          duration: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: this,
            start: () => `top top+=${getHeaderOffset()}`,
            end: () => `+=${getSequenceDistance()}`,
            pin: this.stage,
            pinSpacing: true,
            scrub: 0.7,
            snap: {
              snapTo: 1 / Math.max(1, this.items.length - 1),
              duration: {
                min: 0.18,
                max: 0.48,
              },
              delay: 0.08,
              ease: 'power2.out',
              inertia: false,
            },
            anticipatePin: 1,
            refreshPriority: 10,
            invalidateOnRefresh: true,
            onUpdate: (self) => this.updateItems(this.getActiveIndex(self.progress)),
            onRefresh: (self) => this.updateItems(this.getActiveIndex(self.progress)),
          },
        });
      }, this);

      this.updateItems(0);
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.timeline?.scrollTrigger) return;
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
      });
    }

    destroyAnimation() {
      this.animationContext?.revert();
      this.animationContext = null;
      this.timeline = null;
    }

    enableFallback() {
      if (!this.classList.contains('is-fallback')) this.destroyAnimation();
      this.classList.remove('is-enhanced', 'is-touch-carousel');
      this.classList.add('is-fallback');
      this.layoutMode = this.getLayoutMode();
      this.track.style.removeProperty('transform');
      this.activeIndex = -1;
      this.forceVisible = null;
      this.updateItems(this.getClosestItemIndex(), true);
    }

    enableTouchCarousel() {
      this.destroyAnimation();
      this.classList.add('is-enhanced', 'is-touch-carousel');
      this.classList.remove('is-fallback');
      this.layoutMode = this.getLayoutMode();
      this.track.style.removeProperty('transform');
      this.viewport.scrollLeft = 0;
      this.activeIndex = -1;
      this.forceVisible = null;
      this.updateItems(0);
    }

    getLayoutMode() {
      if (this.desktopQuery.matches) return 'desktop';
      if (this.tabletQuery.matches) return 'tablet';
      return 'mobile';
    }

    handleViewportScroll() {
      if (!this.classList.contains('is-touch-carousel') && !this.classList.contains('is-fallback')) return;
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateItems(
          this.getClosestItemIndex(),
          this.classList.contains('is-fallback')
        );
      });
    }

    handleViewportKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!this.classList.contains('is-touch-carousel') && !this.classList.contains('is-fallback')) return;

      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      this.goToIndex(Number(this.dataset.activeIndex || 0) + direction);
    }

    handlePrevious() {
      this.goToIndex(Number(this.dataset.activeIndex || 0) - 1);
    }

    handleNext() {
      this.goToIndex(Number(this.dataset.activeIndex || 0) + 1);
    }

    goToIndex(index) {
      if (!this.classList.contains('is-touch-carousel') && !this.classList.contains('is-fallback')) return;

      const safeIndex = Math.max(0, Math.min(this.items.length - 1, index));
      const item = this.items[safeIndex];
      if (!item) return;

      const target = item.offsetLeft - (this.viewport.clientWidth - item.clientWidth) / 2;
      this.viewport.scrollTo({
        left: Math.max(0, target),
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }

    getClosestItemIndex() {
      const viewportCenter = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + item.clientWidth / 2;
        const distance = Math.abs(viewportCenter - itemCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    getActiveIndex(progress) {
      const lastIndex = Math.max(0, this.items.length - 1);
      return Math.max(0, Math.min(lastIndex, Math.round(progress * lastIndex)));
    }

    updateItems(activeIndex, forceVisible = false) {
      if (activeIndex === this.activeIndex && forceVisible === this.forceVisible) return;

      this.activeIndex = activeIndex;
      this.forceVisible = forceVisible;
      this.dataset.activeIndex = activeIndex;
      this.updateControls(activeIndex);

      const useDesktopStates = this.layoutMode === 'desktop' && !forceVisible;
      const showAdjacentImages = this.tabletQuery.matches;
      const inactiveScale = Number.parseFloat(
        getComputedStyle(this).getPropertyValue('--generation-scroll-inactive-scale')
      ) || 1;
      this.items.forEach((item, index) => {
        const distance = Math.abs(activeIndex - index);
        const visibility = forceVisible || distance === 0 ? 1 : 0;
        const edgeVisibility = forceVisible || !showAdjacentImages
          ? visibility
          : distance === 1 ? 0.32 : visibility;
        const image = item.querySelector('.generation-scroll__image');
        const caption = item.querySelector('.generation-scroll__model-name');
        const imageScale = inactiveScale + edgeVisibility * (1 - inactiveScale);

        item.toggleAttribute('aria-current', index === activeIndex);

        if (useDesktopStates) {
          item.classList.remove('is-active', 'is-previous', 'is-next', 'is-far', 'is-past');

          if (index === activeIndex) item.classList.add('is-active');
          else if (index === activeIndex - 1) item.classList.add('is-previous');
          else if (index === activeIndex + 1) item.classList.add('is-next');
          else if (index < activeIndex) item.classList.add('is-past');
          else item.classList.add('is-far');

          image?.style.removeProperty('opacity');
          image?.style.removeProperty('filter');
          image?.style.removeProperty('transform');
          caption?.style.removeProperty('opacity');
          caption?.style.removeProperty('filter');
          caption?.style.removeProperty('transform');
          return;
        }

        item.classList.remove('is-active', 'is-previous', 'is-next', 'is-far', 'is-past');

        if (image) {
          image.style.opacity = edgeVisibility.toFixed(3);
          image.style.filter = `blur(${((1 - edgeVisibility) * 18).toFixed(2)}px)`;
          image.style.transform = `translate3d(0, 0, 0) scale(${imageScale.toFixed(3)})`;
        }

        if (caption) {
          caption.style.opacity = visibility.toFixed(3);
          caption.style.filter = `blur(${((1 - visibility) * 8).toFixed(2)}px)`;
          caption.style.transform = `translate3d(0, 0, 0) scale(${(0.92 + visibility * 0.08).toFixed(3)})`;
        }
      });

    }

    updateControls(activeIndex) {
      const supportsNativeNavigation = (
        this.classList.contains('is-touch-carousel')
        || this.classList.contains('is-fallback')
      );
      const showControls = supportsNativeNavigation && this.items.length > 1;

      if (this.previousButton) this.previousButton.hidden = !showControls || activeIndex <= 0;
      if (this.nextButton) this.nextButton.hidden = !showControls || activeIndex >= this.items.length - 1;
    }
  }

  customElements.define('generation-scroll', GenerationScroll);
}
